import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { SrCrewService } from '@app/services/sr-crew.service';
import { SrEquipmentRefundType, SrEquipmentResourceViewGet, SrEquipmentService } from '@app/services/sr-equipment.service';

@Component({
    selector: 'tt-sr-equipment-resource-view',
    templateUrl: './sr-equipment-resource-view.component.html',
    styleUrls: ['./sr-equipment-resource-view.component.css'],
    standalone: false,
})
export class SrEquipmentResourceViewComponent implements OnInit, OnDestroy {
    public page?: SrEquipmentResourceViewGet;
    public mainResources: Item[] = [];
    public subResources: Item[] = [];
    public refundTypes: SrEquipmentRefundType[] = [];
    public items: Item[] = [];
    public error = '';
    private mainVersion = 0;
    private listVersion = 0;

    constructor(private readonly equipment: SrEquipmentService, private readonly crew: SrCrewService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async ngOnInit(): Promise<void> {
        try {
            await Promise.all([
                this.equipment.getResourceView().then(async page => {
                    this.page = page;
                    await Promise.all([this.loadSubResources(), this.loadItems()]);
                }),
                this.crew.getMainResources().then(items => this.mainResources = items),
                this.equipment.getRefundTypes({ add_all: '1' }).then(items => this.refundTypes = items),
            ]);
        } catch (error) { this.showError(error); }
    }

    public async onMainResourceChanged(value: string): Promise<void> {
        if (!this.page || value === this.page.main_resource_id) return;
        this.page.main_resource_id = value;
        try { await this.loadSubResources(); } catch (error) { this.showError(error); }
    }

    public async onSubResourceChanged(value: string): Promise<void> {
        if (!this.page || value === this.page.sub_resource_id) return;
        this.page.sub_resource_id = value;
        try { await this.loadItems(); } catch (error) { this.showError(error); }
    }

    public async onRefundTypeChanged(value: string): Promise<void> {
        if (!this.page || value === this.page.srrefundtype_id) return;
        this.page.srrefundtype_id = value;
        try { await this.loadItems(); } catch (error) { this.showError(error); }
    }

    private async loadSubResources(): Promise<void> {
        const version = ++this.mainVersion;
        const id = this.page?.main_resource_id;
        if (!id?.trim()) return;
        this.subResources = [];
        const items = await this.crew.getSubResources({ main_resource_id: id });
        if (version === this.mainVersion) this.subResources = items;
    }

    private async loadItems(): Promise<void> {
        const version = ++this.listVersion;
        const subId = this.page?.sub_resource_id;
        const refundId = this.page?.srrefundtype_id;
        if (!subId?.trim() || !refundId?.trim()) return;
        this.items = [];
        const items = await this.equipment.getResourceViews({ sub_resource_id: subId, srrefundtype_id: refundId });
        if (version === this.listVersion) this.items = items;
    }

    public openItem(event: { item: Item; event: MouseEvent }): void {
        if (!event.item.item_state) return;
        const to = ['srequipment', 'srequipments', 'srequipmentresourceview'].includes(event.item.item_state) ? `${event.item.item_state}-test` : event.item.item_state;
        const target = { to, parms: event.item.item_parms };
        if (event.event.ctrlKey) this.navigator?.newTab(target); else this.navigator?.go(target);
    }

    public ngOnDestroy(): void { ++this.mainVersion; ++this.listVersion; }
    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
}
